import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import {
  api,
  type AgentJobItem,
  type ExecutiveMemoJobStatusData,
  type NewsJobMode,
  type NewsJobStatusData,
  type NewsNotificationType,
  type ReceiptJobStatusData,
} from "../lib/api";
import { storage } from "../lib/storage";
import { AppError, logError } from "../lib/errors";
import {
  JOB_NOTIFICATION_EVENT,
  getPendingJobNotificationKey,
  readPendingJobNotifications,
  removePendingJobNotification,
  type PendingJobNotification,
} from "../lib/jobNotifications";
import {
  clearPendingNewsJobSnapshot,
  formatNewsJobStageLabel,
  getNewsNotificationTypeLabel,
  readPendingNewsJobSnapshot,
  toPendingNewsJobNotification,
  writePendingNewsJobNotification,
} from "../lib/newsJob";
import {
  clearPendingReceiptJobSnapshot,
  formatReceiptStageLabel,
  getReceiptPollIntervalMs,
  readPendingReceiptJobSnapshot,
  writePendingReceiptJobNotification,
  writePendingReceiptNeedsReviewNotification,
} from "../lib/receipt";
import {
  clearPendingExecutiveMemoJobSnapshot,
  formatExecutiveMemoFailureMessage,
  getExecutiveMemoPollIntervalMs,
  readPendingExecutiveMemoJobSnapshot,
  toPendingExecutiveMemoJobNotification,
  writePendingExecutiveMemoJobNotification,
} from "../lib/executiveMemos";

type JobToastState = {
  key: string;
  tone: "success" | "error" | "warning";
  title: string;
  detail?: string;
  linkTo?: string;
  notification: PendingJobNotification;
};

const SUCCESS_TOAST_DISMISS_MS = 5000;
const FAILURE_TOAST_DISMISS_MS = 8000;

export function getJobFailureMessage(failedError?: string) {
  const message = (failedError ?? "").trim();
  if (!message) return "結果を確認できませんでした。";

  const lowered = message.toLowerCase();
  if (
    lowered.includes("valueerror") ||
    lowered.includes("did not include a json object") ||
    lowered.includes("gemini evidence bundle")
  ) {
    return "ニュース内容の生成に失敗しました。しばらくしてから再試行してください。";
  }

  return message;
}

export function formatCompactNotificationText(value?: string | null, maxLength = 42) {
  const normalized = (value ?? "").replace(/\s+/g, " ").trim();
  if (!normalized) return undefined;
  return normalized.length > maxLength ? `${normalized.slice(0, maxLength)}...` : normalized;
}

function getNewsJobCompletionDetail(
  notification: Extract<PendingJobNotification, { kind: "news" }>,
  contentLabel: string
) {
  const destination = getNewsNotificationTypeLabel(notification.notificationType);
  const keyword = formatCompactNotificationText(notification.keyword);

  if (keyword) {
    return `「${keyword}」の${contentLabel}を${destination}へ送信しました。`;
  }

  return `${destination}への${contentLabel}送信が完了しました。`;
}

function isSuccessToastNotification(notification: PendingJobNotification) {
  if (notification.kind === "level_up" || notification.kind === "mode_unlock") return true;
  if (notification.kind === "receipt_needs_review") return false;
  return notification.status === "completed";
}

function isFailureToastNotification(notification: PendingJobNotification) {
  if (notification.kind === "receipt_needs_review") return false;
  if (notification.kind === "level_up" || notification.kind === "mode_unlock") return false;
  return notification.status === "failed" || notification.status === "timed_out";
}

function isTemporaryToastNotification(notification: PendingJobNotification) {
  return isSuccessToastNotification(notification) || isFailureToastNotification(notification);
}

function buildJobToast(notification: PendingJobNotification): JobToastState {
  if (notification.kind === "level_up") {
    return {
      key: getPendingJobNotificationKey(notification),
      tone: "success",
      title: "ランクアップしました",
      detail: `親密度は${notification.rankName}になりました`,
      notification,
    };
  }

  if (notification.kind === "mode_unlock") {
    return {
      key: getPendingJobNotificationKey(notification),
      tone: "success",
      title: `${notification.modeName ?? "新しい会話モード"}が解放されました`,
      detail: "新しい会話モードを試してください。",
      notification,
    };
  }

  if (notification.kind === "receipt") {
    if (notification.status === "processing") {
      return {
        key: getPendingJobNotificationKey(notification),
        tone: "success",
        title: "領収書を処理中です",
        detail: "画像を解析して保存しています。",
        notification,
      };
    }

    return {
      key: getPendingJobNotificationKey(notification),
      tone: notification.status === "completed" ? "success" : "error",
      title:
        notification.status === "completed"
          ? "領収書の処理が完了しました"
          : notification.status === "needs_input"
            ? "領収書の処理に追加情報が必要です"
          : "領収書の処理に失敗しました",
      detail:
        notification.status === "completed"
          ? notification.extractedSummary ?? "領収書の保存が完了しました。"
          : notification.status === "needs_input"
            ? notification.failedError ?? "内容を確認して、追加情報を入力してください。"
          : notification.failedStage
            ? `${getJobFailureMessage(notification.failedError)} (${formatReceiptStageLabel(
                notification.failedStage
              )})`
            : getJobFailureMessage(notification.failedError),
      linkTo: notification.status === "completed" ? "/receipt/history" : undefined,
      notification,
    };
  }

  if (notification.kind === "receipt_needs_review") {
    return {
      key: getPendingJobNotificationKey(notification),
      tone: "warning",
      title: "確認が必要な領収書があります",
      detail: "読み取り内容を確認してください。",
      linkTo: `/receipt/${notification.jobId}`,
      notification,
    };
  }

  if (notification.kind === "executive_memo") {
    if (notification.status === "processing") {
      return {
        key: getPendingJobNotificationKey(notification),
        tone: "success",
        title: "社長メモを処理中です",
        detail: "画像を解析してメモを整理しています。",
        notification,
      };
    }

    return {
      key: getPendingJobNotificationKey(notification),
      tone: notification.status === "completed" ? "success" : "error",
      title:
        notification.status === "completed"
          ? "社長メモを保存しました"
          : notification.status === "timed_out"
            ? "社長メモの処理がタイムアウトしました"
            : "社長メモを整理できませんでした",
      detail:
        notification.status === "completed"
          ? notification.title ?? notification.summary ?? "社長メモの整理が完了しました。"
          : formatExecutiveMemoFailureMessage(notification.failedError),
      notification,
    };
  }

  const contentLabel = notification.mode === "report" ? "レポート" : "ニュース";

  if (notification.status === "processing") {
    return {
      key: getPendingJobNotificationKey(notification),
      tone: "success",
      title: `${contentLabel}を処理中です`,
      detail: "内容を生成しています。",
      notification,
    };
  }

  return {
    key: getPendingJobNotificationKey(notification),
    tone: notification.status === "completed" ? "success" : "error",
    title:
      notification.status === "completed"
        ? `${contentLabel}送信の処理が完了しました`
        : notification.status === "needs_input"
          ? `${contentLabel}送信に追加情報が必要です`
        : `${contentLabel}送信の処理に失敗しました`,
    detail:
      (() => {
        if (notification.status === "completed") {
          return getNewsJobCompletionDetail(notification, contentLabel);
        }

        if (notification.status === "needs_input") {
          return notification.failedError ?? "内容を確認して、追加情報を入力してください。";
        }

        if (notification.failedStage) {
          return `${getJobFailureMessage(notification.failedError)} (${formatNewsJobStageLabel(
            notification.failedStage
          )})`;
        }

        return getJobFailureMessage(notification.failedError);
      })(),
    notification,
  };
}

function isJobNotFoundError(error: unknown) {
  return error instanceof AppError && error.code === "NOT_FOUND";
}

function toPendingReceiptNotification(response: ReceiptJobStatusData) {
  if (response.status !== "completed" && response.status !== "failed") {
    return null;
  }

  return {
    jobId: response.jobId,
    status: response.status,
    processedAt: response.processedAt ?? undefined,
    extractedSummary: response.extractedSummary ?? undefined,
    failedStage: response.failedStage ?? undefined,
    failedError: response.failedError ?? undefined,
  } as const;
}

function normalizeJobString(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function getAgentJobMeta(item: AgentJobItem): Record<string, unknown> | null {
  if (!item.meta) return null;
  if (isRecord(item.meta)) return item.meta;
  if (typeof item.meta !== "string") return null;

  try {
    const parsed = JSON.parse(item.meta);
    return isRecord(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function getAgentJobString(item: AgentJobItem, keys: string[]): string | undefined {
  const record = item as Record<string, unknown>;
  for (const key of keys) {
    const value = normalizeJobString(record[key]);
    if (value) return value;
  }
  return undefined;
}

function getAgentJobMetaString(item: AgentJobItem, keys: string[]): string | undefined {
  const meta = getAgentJobMeta(item);
  if (!meta) return undefined;
  for (const key of keys) {
    const value = normalizeJobString(meta[key]);
    if (value) return value;
  }
  return undefined;
}

export function getAgentJobMetaBoolean(item: AgentJobItem, keys: string[]): boolean | undefined {
  const meta = getAgentJobMeta(item);
  if (!meta) return undefined;
  for (const key of keys) {
    const value = meta[key];
    if (typeof value === "boolean") return value;
  }
  return undefined;
}

function getCommonAgentJobId(item: AgentJobItem) {
  return getAgentJobString(item, ["jobId", "job_id", "id"]);
}

function getCommonAgentJobStatus(item: AgentJobItem) {
  const rawStatus = getAgentJobString(item, ["status", "rawStatus", "raw_status"]) ?? "";
  const normalized = rawStatus.trim().toLowerCase();
  if (normalized === "pending" || normalized === "requesting") return "accepted";
  if (normalized === "in_progress" || normalized === "runnning") return "running";
  if (normalized === "ambiguous" || normalized === "needs_more_info") return "needs_input";
  return normalized;
}

function getCommonAgentJobProcessedAt(item: AgentJobItem) {
  return getAgentJobString(item, ["processedAt", "processed_at", "updatedAt", "updated_at"]);
}

function getCommonAgentJobSummary(item: AgentJobItem) {
  return getAgentJobString(item, ["resultSummary", "result_summary", "summary"]);
}

function getCommonAgentJobError(item: AgentJobItem) {
  return getAgentJobString(item, ["errorMessage", "error_message"]);
}

function isCommonAgentJobPending(item: AgentJobItem) {
  const status = getCommonAgentJobStatus(item);
  return status === "accepted" || status === "running";
}

function toNotificationStatus(status: string) {
  if (status === "completed" || status === "failed" || status === "needs_input") {
    return status;
  }
  return null;
}

function normalizeNewsNotificationType(value: string | undefined): NewsNotificationType | undefined {
  if (value === "line" || value === "google_chat" || value === "email") {
    return value;
  }
  return undefined;
}

function normalizeNewsMode(value: string | undefined): NewsJobMode | undefined {
  if (value === "news" || value === "report") return value;
  return undefined;
}

export function toPendingReceiptNotificationFromAgentJob(item: AgentJobItem) {
  const status = toNotificationStatus(getCommonAgentJobStatus(item));
  const jobId = getCommonAgentJobId(item);
  if (!status || !jobId) return null;

  const summary = getCommonAgentJobSummary(item);
  const error = getCommonAgentJobError(item) ?? summary;

  return {
    jobId,
    status,
    processedAt: getCommonAgentJobProcessedAt(item),
    extractedSummary: status === "completed" ? summary : undefined,
    failedStage: getAgentJobMetaString(item, ["failed_stage", "failedStage", "stage"]),
    failedError: status === "completed" ? undefined : error,
    needsReview: getAgentJobMetaBoolean(item, ["needsReview", "needs_review"]),
  } as const;
}

function toPendingNewsNotificationFromAgentJob(
  item: AgentJobItem,
  fallbackMode?: NewsJobMode | null,
  fallbackKeyword?: string | null
) {
  const status = toNotificationStatus(getCommonAgentJobStatus(item));
  const jobId = getCommonAgentJobId(item);
  if (!status || !jobId) return null;

  const summary = getCommonAgentJobSummary(item);
  const error = getCommonAgentJobError(item) ?? summary;
  const resource = getAgentJobString(item, ["resource"]);
  const metaMode = normalizeNewsMode(
    getAgentJobMetaString(item, ["mode", "news_mode", "newsMode"])
  );

  return {
    jobId,
    status,
    processedAt: getCommonAgentJobProcessedAt(item),
    keyword:
      formatCompactNotificationText(fallbackKeyword, 80) ??
      getAgentJobMetaString(item, ["keyword", "topic", "theme"]) ??
      formatCompactNotificationText(getAgentJobString(item, ["summary"]), 80) ??
      getAgentJobString(item, ["title"]),
    notificationType: normalizeNewsNotificationType(
      getAgentJobMetaString(item, ["notification_type", "notificationType"])
    ),
    mode: resource === "report" ? "report" : metaMode ?? fallbackMode ?? undefined,
    failedStage: getAgentJobMetaString(item, ["failed_stage", "failedStage", "stage"]),
    failedError: status === "completed" ? undefined : error,
    summary: status === "completed" ? summary : undefined,
  } as const;
}

async function fetchCommonAgentJob(resource: "news" | "report" | "receipt", jobId: string) {
  const getAgentJob = api.getAgentJob as unknown;
  if (typeof getAgentJob !== "function") {
    return null;
  }

  try {
    return await getAgentJob(resource, jobId) as AgentJobItem;
  } catch (error) {
    if (isJobNotFoundError(error)) {
      return null;
    }
    throw error;
  }
}

type PendingReceiptAgentJobNotification = NonNullable<
  ReturnType<typeof toPendingReceiptNotificationFromAgentJob>
>;
type PendingNewsAgentJobNotification = NonNullable<
  ReturnType<typeof toPendingNewsNotificationFromAgentJob>
>;

export function JobNotificationCenter() {
  const location = useLocation();
  const receiptPollTimerRef = useRef<number | null>(null);
  const executiveMemoPollTimerRef = useRef<number | null>(null);
  const newsPollTimerRef = useRef<number | null>(null);
  const dismissTimerRef = useRef<number | null>(null);
  const routeKeyRef = useRef(`${location.pathname}${location.search}${location.hash}`);
  const [notifications, setNotifications] = useState<PendingJobNotification[]>([]);

  const syncNotifications = useCallback(() => {
    setNotifications(readPendingJobNotifications());
  }, []);

  const dismissToast = useCallback((notification: PendingJobNotification) => {
    if (notification.kind === "mode_unlock") {
      storage.addAcknowledgedUnlockedMode(notification.characterId, notification.mode);
    }
    removePendingJobNotification(notification);
  }, []);

  const stopReceiptPolling = useCallback(() => {
    if (receiptPollTimerRef.current !== null) {
      window.clearTimeout(receiptPollTimerRef.current);
      receiptPollTimerRef.current = null;
    }
  }, []);

  const stopNewsPolling = useCallback(() => {
    if (newsPollTimerRef.current !== null) {
      window.clearTimeout(newsPollTimerRef.current);
      newsPollTimerRef.current = null;
    }
  }, []);

  const stopExecutiveMemoPolling = useCallback(() => {
    if (executiveMemoPollTimerRef.current !== null) {
      window.clearTimeout(executiveMemoPollTimerRef.current);
      executiveMemoPollTimerRef.current = null;
    }
  }, []);

  const handleResolvedReceiptJob = useCallback(
    (response: ReceiptJobStatusData) => {
      const notification = toPendingReceiptNotification(response);
      if (!notification) {
        return;
      }

      clearPendingReceiptJobSnapshot();
      stopReceiptPolling();
      writePendingReceiptJobNotification(notification);
      if (response.status === "completed" && response.needsReview) {
        writePendingReceiptNeedsReviewNotification(response.jobId);
      }
    },
    [stopReceiptPolling]
  );

  const handleResolvedExecutiveMemoJob = useCallback(
    (response: ExecutiveMemoJobStatusData) => {
      const notification = toPendingExecutiveMemoJobNotification(response);
      if (!notification) {
        return;
      }

      clearPendingExecutiveMemoJobSnapshot();
      stopExecutiveMemoPolling();
      writePendingExecutiveMemoJobNotification(notification);
    },
    [stopExecutiveMemoPolling]
  );

  const handleResolvedNewsJob = useCallback(
    (response: NewsJobStatusData) => {
      const pendingSnapshot = readPendingNewsJobSnapshot();
      const notification = toPendingNewsJobNotification(response, {
        mode: pendingSnapshot?.mode,
      });
      if (!notification) {
        return;
      }

      clearPendingNewsJobSnapshot();
      stopNewsPolling();
      writePendingNewsJobNotification(notification);
    },
    [stopNewsPolling]
  );

  const handleResolvedReceiptAgentJob = useCallback(
    (notification: PendingReceiptAgentJobNotification) => {
      clearPendingReceiptJobSnapshot();
      stopReceiptPolling();
      writePendingReceiptJobNotification(notification);
      if (notification.status === "completed" && notification.needsReview) {
        writePendingReceiptNeedsReviewNotification(notification.jobId);
      }
    },
    [stopReceiptPolling]
  );

  const handleResolvedNewsAgentJob = useCallback(
    (notification: PendingNewsAgentJobNotification) => {
      clearPendingNewsJobSnapshot();
      stopNewsPolling();
      writePendingNewsJobNotification(notification);
    },
    [stopNewsPolling]
  );

  const scheduleReceiptJobPoll = useCallback(
    (jobId: string, startedAtMs: number, clientRequestId?: string) => {
      stopReceiptPolling();
      const pollIntervalMs = getReceiptPollIntervalMs(Math.max(0, Date.now() - startedAtMs));

      receiptPollTimerRef.current = window.setTimeout(async () => {
        try {
          const commonResponse = await fetchCommonAgentJob("receipt", jobId);
          if (commonResponse) {
            if (isCommonAgentJobPending(commonResponse)) {
              scheduleReceiptJobPoll(jobId, startedAtMs, clientRequestId);
              return;
            }

            const notification = toPendingReceiptNotificationFromAgentJob(commonResponse);
            if (notification) {
              handleResolvedReceiptAgentJob(notification);
              return;
            }
          }

          const response = await api.getReceiptJob(jobId);

          if (response.status === "accepted") {
            scheduleReceiptJobPoll(jobId, startedAtMs, response.clientRequestId ?? clientRequestId);
            return;
          }

          handleResolvedReceiptJob(response);
        } catch (error) {
          if (isJobNotFoundError(error)) {
            clearPendingReceiptJobSnapshot();
            stopReceiptPolling();
            return;
          }

          logError(error);
          scheduleReceiptJobPoll(jobId, startedAtMs, clientRequestId);
        }
      }, pollIntervalMs);
    },
    [handleResolvedReceiptAgentJob, handleResolvedReceiptJob, stopReceiptPolling]
  );

  const scheduleExecutiveMemoJobPoll = useCallback(
    (jobId: string, startedAtMs: number, clientRequestId?: string) => {
      stopExecutiveMemoPolling();
      const pollIntervalMs = getExecutiveMemoPollIntervalMs(Math.max(0, Date.now() - startedAtMs));

      executiveMemoPollTimerRef.current = window.setTimeout(async () => {
        try {
          const response = await api.getExecutiveMemoJob(jobId);

          if (response.relayStatus === "accepted" || response.relayStatus === "processing") {
            scheduleExecutiveMemoJobPoll(
              jobId,
              startedAtMs,
              response.clientRequestId ?? clientRequestId
            );
            return;
          }

          handleResolvedExecutiveMemoJob(response);
        } catch (error) {
          if (isJobNotFoundError(error)) {
            clearPendingExecutiveMemoJobSnapshot();
            stopExecutiveMemoPolling();
            return;
          }

          logError(error);
          scheduleExecutiveMemoJobPoll(jobId, startedAtMs, clientRequestId);
        }
      }, pollIntervalMs);
    },
    [handleResolvedExecutiveMemoJob, stopExecutiveMemoPolling]
  );

  const scheduleNewsJobPoll = useCallback(
    (jobId: string, startedAtMs: number) => {
      stopNewsPolling();
      const pollIntervalMs = getReceiptPollIntervalMs(Math.max(0, Date.now() - startedAtMs));

      newsPollTimerRef.current = window.setTimeout(async () => {
        try {
          const pendingSnapshot = readPendingNewsJobSnapshot();
          const resource = pendingSnapshot?.mode === "report" ? "report" : "news";
          const commonResponse = await fetchCommonAgentJob(resource, jobId);
          if (commonResponse) {
            if (isCommonAgentJobPending(commonResponse)) {
              scheduleNewsJobPoll(jobId, startedAtMs);
              return;
            }

            const notification = toPendingNewsNotificationFromAgentJob(
              commonResponse,
              pendingSnapshot?.mode,
              pendingSnapshot?.keyword
            );
            if (notification) {
              handleResolvedNewsAgentJob(notification);
              return;
            }
          }

          const response = await api.getNewsJob(jobId);

          if (response.status === "accepted") {
            scheduleNewsJobPoll(jobId, startedAtMs);
            return;
          }

          handleResolvedNewsJob(response);
        } catch (error) {
          if (isJobNotFoundError(error)) {
            clearPendingNewsJobSnapshot();
            stopNewsPolling();
            return;
          }

          logError(error);
          scheduleNewsJobPoll(jobId, startedAtMs);
        }
      }, pollIntervalMs);
    },
    [handleResolvedNewsAgentJob, handleResolvedNewsJob, stopNewsPolling]
  );

  const refreshPendingReceiptJob = useCallback(async () => {
    const pendingSnapshot = readPendingReceiptJobSnapshot();
    if (!pendingSnapshot?.jobId) {
      stopReceiptPolling();
      return;
    }

    const startedAtMs = Date.parse(pendingSnapshot.startedAt);
    const safeStartedAtMs = Number.isFinite(startedAtMs) ? startedAtMs : Date.now();

    stopReceiptPolling();

    try {
      const commonResponse = await fetchCommonAgentJob("receipt", pendingSnapshot.jobId);
      if (commonResponse) {
        if (isCommonAgentJobPending(commonResponse)) {
          scheduleReceiptJobPoll(
            pendingSnapshot.jobId,
            safeStartedAtMs,
            pendingSnapshot.clientRequestId
          );
          return;
        }

        const notification = toPendingReceiptNotificationFromAgentJob(commonResponse);
        if (notification) {
          handleResolvedReceiptAgentJob(notification);
          return;
        }
      }

      const response = await api.getReceiptJob(pendingSnapshot.jobId);

      if (response.status === "accepted") {
        scheduleReceiptJobPoll(
          pendingSnapshot.jobId,
          safeStartedAtMs,
          response.clientRequestId ?? pendingSnapshot.clientRequestId
        );
        return;
      }

      handleResolvedReceiptJob(response);
    } catch (error) {
      if (isJobNotFoundError(error)) {
        clearPendingReceiptJobSnapshot();
        stopReceiptPolling();
        return;
      }

      logError(error);
      scheduleReceiptJobPoll(
        pendingSnapshot.jobId,
        safeStartedAtMs,
        pendingSnapshot.clientRequestId
      );
    }
  }, [
    handleResolvedReceiptAgentJob,
    handleResolvedReceiptJob,
    scheduleReceiptJobPoll,
    stopReceiptPolling,
  ]);

  const refreshPendingExecutiveMemoJob = useCallback(async () => {
    const pendingSnapshot = readPendingExecutiveMemoJobSnapshot();
    if (!pendingSnapshot?.jobId) {
      stopExecutiveMemoPolling();
      return;
    }

    const startedAtMs = Date.parse(pendingSnapshot.startedAt);
    const safeStartedAtMs = Number.isFinite(startedAtMs) ? startedAtMs : Date.now();

    stopExecutiveMemoPolling();

    try {
      const response = await api.getExecutiveMemoJob(pendingSnapshot.jobId);

      if (response.relayStatus === "accepted" || response.relayStatus === "processing") {
        scheduleExecutiveMemoJobPoll(
          pendingSnapshot.jobId,
          safeStartedAtMs,
          response.clientRequestId ?? pendingSnapshot.clientRequestId
        );
        return;
      }

      handleResolvedExecutiveMemoJob(response);
    } catch (error) {
      if (isJobNotFoundError(error)) {
        clearPendingExecutiveMemoJobSnapshot();
        stopExecutiveMemoPolling();
        return;
      }

      logError(error);
      scheduleExecutiveMemoJobPoll(
        pendingSnapshot.jobId,
        safeStartedAtMs,
        pendingSnapshot.clientRequestId
      );
    }
  }, [
    handleResolvedExecutiveMemoJob,
    scheduleExecutiveMemoJobPoll,
    stopExecutiveMemoPolling,
  ]);

  const refreshPendingNewsJob = useCallback(async () => {
    const pendingSnapshot = readPendingNewsJobSnapshot();
    if (!pendingSnapshot?.jobId) {
      stopNewsPolling();
      return;
    }

    const startedAtMs = Date.parse(pendingSnapshot.startedAt);
    const safeStartedAtMs = Number.isFinite(startedAtMs) ? startedAtMs : Date.now();

    stopNewsPolling();

    try {
      const resource = pendingSnapshot.mode === "report" ? "report" : "news";
      const commonResponse = await fetchCommonAgentJob(resource, pendingSnapshot.jobId);
      if (commonResponse) {
        if (isCommonAgentJobPending(commonResponse)) {
          scheduleNewsJobPoll(pendingSnapshot.jobId, safeStartedAtMs);
          return;
        }

        const notification = toPendingNewsNotificationFromAgentJob(
          commonResponse,
          pendingSnapshot.mode,
          pendingSnapshot.keyword
        );
        if (notification) {
          handleResolvedNewsAgentJob(notification);
          return;
        }
      }

      const response = await api.getNewsJob(pendingSnapshot.jobId);

      if (response.status === "accepted") {
        scheduleNewsJobPoll(pendingSnapshot.jobId, safeStartedAtMs);
        return;
      }

      handleResolvedNewsJob(response);
    } catch (error) {
      if (isJobNotFoundError(error)) {
        clearPendingNewsJobSnapshot();
        stopNewsPolling();
        return;
      }

      logError(error);
      scheduleNewsJobPoll(pendingSnapshot.jobId, safeStartedAtMs);
    }
  }, [handleResolvedNewsAgentJob, handleResolvedNewsJob, scheduleNewsJobPoll, stopNewsPolling]);

  useEffect(() => {
    syncNotifications();
  }, [syncNotifications]);

  useEffect(() => {
    if (dismissTimerRef.current !== null) {
      window.clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
    }

    if (!notifications.length) {
      return;
    }

    const dismissTarget = notifications.find(isTemporaryToastNotification);
    if (!dismissTarget) {
      return;
    }

    dismissTimerRef.current = window.setTimeout(() => {
      dismissToast(dismissTarget);
    }, isFailureToastNotification(dismissTarget) ? FAILURE_TOAST_DISMISS_MS : SUCCESS_TOAST_DISMISS_MS);

    return () => {
      if (dismissTimerRef.current !== null) {
        window.clearTimeout(dismissTimerRef.current);
        dismissTimerRef.current = null;
      }
    };
  }, [dismissToast, notifications]);

  useEffect(() => {
    const routeKey = `${location.pathname}${location.search}${location.hash}`;
    if (routeKeyRef.current === routeKey) {
      return;
    }

    routeKeyRef.current = routeKey;
    const temporaryNotifications = readPendingJobNotifications().filter(isTemporaryToastNotification);
    if (!temporaryNotifications.length) {
      return;
    }

    temporaryNotifications.forEach(removePendingJobNotification);
    syncNotifications();
  }, [location.hash, location.pathname, location.search, syncNotifications]);

  useEffect(() => {
    const handleNotificationEvent = () => {
      syncNotifications();
      if (location.pathname !== "/request/executive-memos") {
        void refreshPendingExecutiveMemoJob();
      }
    };

    window.addEventListener(JOB_NOTIFICATION_EVENT, handleNotificationEvent as EventListener);

    return () => {
      window.removeEventListener(JOB_NOTIFICATION_EVENT, handleNotificationEvent as EventListener);
    };
  }, [location.pathname, refreshPendingExecutiveMemoJob, syncNotifications]);

  useEffect(() => {
    if (location.pathname === "/receipt") {
      stopReceiptPolling();
    } else {
      void refreshPendingReceiptJob();
    }

    if (location.pathname === "/request/executive-memos") {
      stopExecutiveMemoPolling();
    } else {
      void refreshPendingExecutiveMemoJob();
    }

    void refreshPendingNewsJob();

    const handleFocus = () => {
      if (location.pathname !== "/receipt") {
        void refreshPendingReceiptJob();
      }
      if (location.pathname !== "/request/executive-memos") {
        void refreshPendingExecutiveMemoJob();
      }
      void refreshPendingNewsJob();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        handleFocus();
      }
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      stopReceiptPolling();
      stopExecutiveMemoPolling();
      stopNewsPolling();
    };
  }, [
    location.pathname,
    refreshPendingExecutiveMemoJob,
    refreshPendingNewsJob,
    refreshPendingReceiptJob,
    stopExecutiveMemoPolling,
    stopNewsPolling,
    stopReceiptPolling,
  ]);

  if (!notifications.length) {
    return null;
  }

  return (
    <div className="pointer-events-none fixed inset-x-0 top-4 z-50 box-border flex justify-center px-4 sm:justify-end sm:px-6">
      <div
        data-testid="job-notification-stack"
        className="box-border flex w-full max-w-[calc(100vw-2rem)] flex-col gap-3 sm:max-w-md"
      >
        {notifications.map((notification) => {
          const toast = buildJobToast(notification);

          return (
            <div
              key={toast.key}
              className={`pointer-events-auto box-border w-full max-w-full rounded-[1.4rem] border px-4 py-3 shadow-[0_18px_40px_rgba(74,62,57,0.16)] ${
                toast.tone === "success"
                  ? "border-[#D8E7D3] bg-[#F7FBF3] text-[#4B6650]"
                  : toast.tone === "warning"
                    ? "border-[#F0D9A8] bg-[#FFF8E8] text-[#8A6A2E]"
                    : "border-[#E8B2A8] bg-[#FFF3F1] text-[#9A4B40]"
              }`}
            >
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <p className="break-words text-sm font-black [overflow-wrap:anywhere]">{toast.title}</p>
                  {toast.detail ? (
                    <p className="mt-1 whitespace-pre-wrap break-words text-sm leading-6 [overflow-wrap:anywhere]">
                      {toast.detail}
                    </p>
                  ) : null}
                </div>
                <button
                  type="button"
                  aria-label="通知を閉じる"
                  onClick={() => dismissToast(notification)}
                  className="shrink-0 rounded-full border border-current/20 bg-white/70 px-2 py-1 text-xs font-black transition hover:bg-white"
                >
                  閉じる
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
